/**
 * ============================================================================
 * PROTECTED FEATURE HOOK - useProtectedFeature
 * ============================================================================
 * Manages Microsoft Entra ID authentication for user-facing protected features.
 * 
 * Features protected:
 * - Leaderboard
 * - User Inbox
 * - User Map
 * 
 * Security Pattern (Josh's Approach):
 * 1. Frontend checks if user is authenticated (instant UX feedback)
 * 2. If not authenticated → Trigger Microsoft login popup
 * 3. After login, inject Bearer token into API requests
 * 4. Backend verifies token and checks TID (tenant ID)
 * 5. Only PBFS tenant users can access protected features
 * 
 * Author: Matt Hales (with Josh's security pattern)
 * Created: January 26, 2026
 */

import { useState, useCallback, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../authConfig';

export const useProtectedFeature = () => {
  const { instance, accounts, inProgress } = useMsal();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState(null);
  
  const isNativePlatform = window.Capacitor?.isNativePlatform?.() === true;
  const hasNativeToken = isNativePlatform && !!localStorage.getItem('token');

  // MSAL accounts may be empty after native iOS auth + page reload because
  // handleRedirectPromise(hash) doesn't persist to MSAL's cache the same way
  // a browser redirect does. Fall back to checking localStorage token on native.
  const isAuthenticated = (accounts.length > 0 && inProgress === 'none') || hasNativeToken;
  
  // Get current user info
  const currentUser = accounts.length > 0 ? accounts[0] : null;
  
  /**
   * Get access token for API calls
   * On native platforms, falls back to the stored token when MSAL cache is empty.
   */
  const getAccessToken = useCallback(async () => {
    // Native fallback: use stored token directly when MSAL cache is empty
    if (isNativePlatform && accounts.length === 0) {
      const storedToken = localStorage.getItem('token');
      if (storedToken) return storedToken;
    }

    if (accounts.length === 0) {
      throw new Error('Not authenticated');
    }
    
    try {
      const response = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      
      return response.idToken;
    } catch (error) {
      if (error.name === 'InteractionRequiredAuthError') {
        // On native, fall back to stored token instead of popup
        if (isNativePlatform) {
          const storedToken = localStorage.getItem('token');
          if (storedToken) return storedToken;
        }
        const response = await instance.acquireTokenPopup({
          ...loginRequest,
          account: accounts[0]
        });
        return response.idToken;
      }
      throw error;
    }
  }, [instance, accounts, isNativePlatform]);
  
  /**
   * Authenticate user via Microsoft login popup
   * Returns: Promise<boolean> - true if successful, false if canceled
   */
  const authenticate = useCallback(async () => {
    if (isAuthenticating) {
      console.warn('⚠️ Authentication already in progress');
      return false;
    }
    
    setIsAuthenticating(true);
    setAuthError(null);
    
    const isCapacitor = window.Capacitor?.isNativePlatform?.() === true;
    const isIOSNative = isCapacitor && window.Capacitor?.getPlatform?.() === 'ios';
    const isMobile = isCapacitor || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
    
    if (isIOSNative) {
      // iOS native: Use ASWebAuthenticationSession via local Capacitor plugin
      try {
        const NativeAuth = (await import('../utils/nativeAuth')).default;

        // Pre-fill the Microsoft login with the onboarding email if available
        const onboardingEmail = localStorage.getItem('submitted_on_behalf_of')
          || localStorage.getItem('submittedOnBehalfOf')
          || '';

        // MSAL generates the full OAuth URL with PKCE and stores the code_verifier
        window.__msalPendingAuthUrl = null;
        await instance.loginRedirect({
          ...loginRequest,
          prompt: 'select_account',
          loginHint: onboardingEmail || undefined
        });
        // onRedirectNavigate captured the URL and returned false, so loginRedirect resolves

        let authUrl = window.__msalPendingAuthUrl;
        if (!authUrl) {
          throw new Error('MSAL did not generate auth URL');
        }

        // MSAL.js forces response_mode=fragment (SPA default), but Azure AD requires
        // response_mode=query for Mobile/Desktop redirect URIs (custom URL schemes).
        authUrl = authUrl.replace('response_mode=fragment', 'response_mode=query');

        console.log('📱 Opening ASWebAuthenticationSession...');
        const result = await NativeAuth.openAuthSession({
          url: authUrl,
          callbackScheme: 'msauth.com.pyebarkerfs.tagquest'
        });

        console.log('📱 Auth session returned callback URL:', result.url?.substring(0, 80));

        // Extract auth params from callback URL and convert to hash for MSAL.js
        const callbackUrl = new URL(result.url);
        const params = callbackUrl.search || '';
        const hashFromQuery = params.startsWith('?') ? '#' + params.substring(1) : callbackUrl.hash;
        console.log('📱 Passing auth response to MSAL handleRedirectPromise...');

        const response = await instance.handleRedirectPromise(hashFromQuery);
        if (response?.account) {
          if (response.idToken) {
            localStorage.setItem('token', response.idToken);
          }

          const acctName = response.account.name || response.account.username || 'User';
          const acctEmail = response.account.username || '';
          localStorage.setItem('currentUser', acctName);
          localStorage.setItem('submitted_on_behalf_of', acctEmail);
          localStorage.setItem('user_profile', JSON.stringify({
            name: acctName,
            email: acctEmail,
            displayName: acctName
          }));

          console.log('✅ iOS auth complete:', acctEmail);
          setIsAuthenticating(false);

          // Reload so all hooks (gamification, auth) pick up the new session
          // This mirrors the web redirect flow behavior
          window.location.reload();
          return true;
        }

        console.log('⚠️ handleRedirectPromise returned null after iOS auth');
        setIsAuthenticating(false);
        return false;
      } catch (error) {
        if (error?.code === 'USER_CANCELLED') {
          setAuthError('Login canceled');
        } else {
          console.error('❌ iOS native auth error:', error);
          setAuthError(error.message || 'Authentication failed');
        }
        setIsAuthenticating(false);
        return false;
      }
    }
    
    if (isMobile) {
      // Android / mobile web: standard loginRedirect (works fine in real browsers)
      try {
        const intendedPath = window.location.pathname;
        if (intendedPath && intendedPath !== '/') {
          sessionStorage.setItem('msal_redirect_target', intendedPath);
        }
        await instance.loginRedirect({
          ...loginRequest,
          prompt: 'select_account'
        });
        return false;
      } catch (error) {
        console.error('❌ Mobile redirect auth error:', error);
        setAuthError(error.message || 'Authentication failed');
        setIsAuthenticating(false);
        return false;
      }
    }
    
    try {
      const response = await instance.loginPopup({
        ...loginRequest,
        prompt: 'select_account'
      });
      
      if (response && response.account) {
        if (response.idToken) {
          localStorage.setItem('token', response.idToken);
        }
        
        setIsAuthenticating(false);
        return true;
      }
      
      setIsAuthenticating(false);
      return false;
      
    } catch (error) {
      console.error('❌ Authentication error:', error);
      
      if (error.errorCode === 'user_cancelled' || error.message.includes('AADB2C90091')) {
        setAuthError('Login canceled');
        setIsAuthenticating(false);
        return false;
      }
      
      setAuthError(error.message || 'Authentication failed');
      setIsAuthenticating(false);
      return false;
    }
  }, [instance, isAuthenticating]);
  
  /**
   * Call a protected API endpoint with automatic auth handling
   * 
   * @param {string} endpoint - API endpoint to call
   * @param {object} options - Fetch options (method, body, etc.)
   * @param {function} onSuccess - Callback on successful response
   * @param {function} onError - Callback on error
   * @returns {Promise<any>} - API response data
   */
  const callProtectedAPI = useCallback(async (endpoint, options = {}, onSuccess, onError) => {
    try {
      // Check if authenticated
      if (!isAuthenticated) {
        console.log('🔐 Not authenticated - prompting for login...');
        const authResult = await authenticate();
        
        if (!authResult) {
          // User canceled or auth failed
          if (onError) onError(new Error('Authentication required'));
          return null;
        }
      }
      
      // Get access token
      const token = await getAccessToken();
      
      // Make API request with Bearer token
      const response = await fetch(endpoint, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        // Handle 401 (token expired/invalid) - retry once with new token
        if (response.status === 401) {
          console.log('🔐 Token invalid/expired - refreshing...');
          const newToken = await getAccessToken();
          
          const retryResponse = await fetch(endpoint, {
            ...options,
            headers: {
              ...options.headers,
              'Authorization': `Bearer ${newToken}`,
              'Content-Type': 'application/json',
            }
          });
          
          const retryData = await retryResponse.json();
          
          if (!retryResponse.ok) {
            throw new Error(retryData.detail || 'API request failed');
          }
          
          if (onSuccess) onSuccess(retryData);
          return retryData;
        }
        
        // Handle 403 (wrong tenant)
        if (response.status === 403) {
          throw new Error('Access denied. This feature is only available to Pye-Barker Fire & Safety users.');
        }
        
        throw new Error(data.detail || `API error: ${response.status}`);
      }
      
      if (onSuccess) onSuccess(data);
      return data;
      
    } catch (error) {
      console.error('❌ Protected API call failed:', error);
      if (onError) onError(error);
      throw error;
    }
  }, [isAuthenticated, authenticate, getAccessToken]);
  
  /**
   * Logout user
   */
  const logout = useCallback(async () => {
    try {
      await instance.logoutPopup();
      localStorage.removeItem('token');
      console.log('✅ Logged out successfully');
    } catch (error) {
      console.error('❌ Logout error:', error);
    }
  }, [instance]);
  
  // Auto-refresh token before it expires (every 50 minutes)
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const interval = setInterval(async () => {
      try {
        console.log('🔄 Refreshing access token...');
        await getAccessToken();
      } catch (error) {
        console.error('❌ Token refresh failed:', error);
      }
    }, 50 * 60 * 1000); // 50 minutes
    
    return () => clearInterval(interval);
  }, [isAuthenticated, getAccessToken]);
  
  return {
    // Auth state
    isAuthenticated,
    isAuthenticating,
    authError,
    currentUser,
    
    // Auth methods
    authenticate,
    logout,
    getAccessToken,
    
    // Protected API helper
    callProtectedAPI,
  };
};

export default useProtectedFeature;
